import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, MessageSquare, Send, Store, User } from 'lucide-react';
import { Button, Card, Skeleton, Textarea, cn } from '@mlm/ui';
import {
  listAdminConversations,
  listAdminChatMessages,
  markAdminConversationRead,
  sendAdminChatMessage,
  setUserChatBlocked,
  type AdminConversationListItem,
} from '../lib/chat-api';

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Chat (M-chat) shared house support inbox. Split pane: conversation list (slow
 * 30s poll) + the selected thread (fast 4s poll). Any admin with `view.messages`
 * sees + answers here; the seller read-cursor is shared across the team.
 */
export function AdminMessagesPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const convosQ = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => listAdminConversations({ limit: 40 }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const items = convosQ.data?.items ?? [];
  const selected = items.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Messages</h1>
        <span className="text-sm text-muted-foreground">Customer support inbox</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
        <Card className="h-[calc(100vh-12rem)] overflow-y-auto">
          {convosQ.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
              <MessageSquare className="h-7 w-7" />
              <p className="text-sm">No customer messages yet.</p>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/50',
                      selectedId === c.id && 'bg-muted',
                    )}
                  >
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                      <User className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn('truncate text-sm', c.unread && 'font-semibold')}>
                          {c.counterparty ?? 'Customer'}
                        </span>
                        <span className="flex-none text-[11px] text-muted-foreground">
                          {relativeTime(c.lastMessageAt)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <p
                          className={cn(
                            'min-w-0 flex-1 truncate text-xs',
                            c.unread ? 'text-foreground' : 'text-muted-foreground',
                          )}
                        >
                          {c.lastMessageSenderKind === 'seller' && 'You: '}
                          {c.lastMessagePreview ?? 'No messages yet'}
                        </p>
                        {c.unread && (
                          <span className="h-2 w-2 flex-none rounded-full bg-primary" aria-hidden />
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="h-[calc(100vh-12rem)]">
          {selected ? (
            <AdminThread conversation={selected} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Store className="h-8 w-8" />
              <p className="text-sm">Select a conversation to reply.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function AdminThread({ conversation }: { conversation: AdminConversationListItem }) {
  const id = conversation.id;
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const msgsQ = useInfiniteQuery({
    queryKey: ['chat', 'thread', id, 'messages'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listAdminChatMessages(id, { limit: 30, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 4_000,
    refetchOnWindowFocus: true,
  });
  const messages = useMemo(
    () => (msgsQ.data?.pages.flatMap((p) => p.items) ?? []).slice().reverse(),
    [msgsQ.data],
  );

  const markRead = useMutation({
    mutationFn: () => markAdminConversationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'unread-count'] });
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    },
  });
  const lastIncomingId = messages.filter((m) => m.senderKind === 'buyer').at(-1)?.id;
  useEffect(() => {
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lastIncomingId]);

  const send = useMutation({
    mutationFn: (body: string) => sendAdminChatMessage(id, body),
    meta: { successMessage: 'Message sent.' },
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['chat', 'thread', id, 'messages'] });
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const blockToggle = useMutation({
    mutationFn: () => setUserChatBlocked(conversation.buyerUserId, !conversation.buyerChatBlocked),
    meta: {
      successMessage: conversation.buyerChatBlocked
        ? 'Member unblocked.'
        : 'Member blocked from chat.',
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', 'conversations'] }),
  });

  function submit() {
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="flex items-center gap-2 border-b pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          <User className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{conversation.counterparty ?? 'Customer'}</div>
          {conversation.buyerChatBlocked && (
            <div className="text-xs text-destructive">Blocked from messaging</div>
          )}
        </div>
        {/* Block / Unblock — only meaningful for member (house) buyers. */}
        {conversation.sellerKind === 'house' && (
          <Button
            variant="outline"
            size="sm"
            className="flex-none"
            disabled={blockToggle.isPending}
            onClick={() => {
              const verb = conversation.buyerChatBlocked ? 'Unblock' : 'Block';
              if (
                confirm(`${verb} ${conversation.counterparty ?? 'this member'} from messaging?`)
              ) {
                blockToggle.mutate();
              }
            }}
          >
            <Ban className="mr-1 h-4 w-4" />
            {conversation.buyerChatBlocked ? 'Unblock' : 'Block'}
          </Button>
        )}
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto py-4">
        {msgsQ.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              disabled={msgsQ.isFetchingNextPage}
              onClick={() => msgsQ.fetchNextPage()}
            >
              Load earlier messages
            </Button>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn('flex', m.senderKind === 'seller' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'max-w-[75%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm',
                m.senderKind === 'seller'
                  ? 'rounded-br-sm bg-primary text-primary-foreground'
                  : 'rounded-bl-sm bg-muted text-foreground',
              )}
            >
              {m.body}
              <span
                className={cn(
                  'mt-1 block text-right text-[10px]',
                  m.senderKind === 'seller'
                    ? 'text-primary-foreground/70'
                    : 'text-muted-foreground',
                )}
              >
                {relativeTime(m.createdAt)}
              </span>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex items-end gap-2 border-t pt-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Type a reply…"
          rows={1}
          maxLength={4000}
          className="max-h-32 min-h-[2.5rem] flex-1 resize-none"
        />
        <Button onClick={submit} disabled={!draft.trim() || send.isPending} className="flex-none">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
